// SEAT-DRAWER-MODALS (Andrei, 2026-10-28): what each seat-panel section's gear opens. Every modal
// configures its section over the chart through the routes and dialogs the rest of the app
// already uses; a save closes the dialog it was made in and refreshes the chart's reads (the
// cards and the panel), and the panel stays open on its seat.
//   - Seat setup (SEAT-DRAWER-UX: the checklist behind the header's ring, a popover): the
//     required items, each in place, missing (with its add) or hidden.
//   - Performance: the seat's KPIs (the seat page's KPIs panel: add, edit, remove, target,
//     cadence, scoring) and this week's values, typed in place.
//   - Execution: the measurables' targets and attainment (edit a target), and the Execution bands.
//   - Responsibilities: the seat page's list (add, edit, archive, delete, recurring work), each
//     linking the seat's measurables.
//   - Tasks: the seat's open tasks, reassigned in place, and a new task for the seat.
//   - Finance: the seat's budget for the month (edit it, or set one).
//   - Priorities: the quarter's priorities: add, edit, track, remove.
//   - Overall: the lens weights of the Overall score (company-wide, as Setup › Chart).
import {
  type CompletenessItem,
  lensThresholdsOf,
  lensWeightsOf,
  SCORED_LENSES,
  type ScoredLens,
} from "@companyos/domain-work/org";
import {
  Badge,
  Button,
  cn,
  EmptyState,
  Input,
  LensBandDot,
  NativeSelect,
  NativeSelectOption,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckIcon, LockIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { Goal, ScorecardMetric, SeatMetric, TargetRule } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { currentWeek, weekRangeLabel } from "../../focus/weeks";
import type { CompanyAccess } from "../company";
import { correctionNeedsReason, editableMetric } from "../company/scorecard-model";
import { formatCardMetric, formatMetricValue } from "../format";
import { KpiAssignDialog } from "../kpi/kpi-assign-dialog";
import { seatSetQuery, seatTargetsQuery, useAllMetrics } from "../kpi/queries";
import { holderText, type SeatHolder, SeatKpisPanel } from "../kpi/seat-kpis";
import { SeatNumbers } from "../numbers/my-numbers";
import { companyQuery, keys, myNumbersQuery, seatScorecardQuery } from "../queries";
import { Loading } from "../screen-parts";
import { RowAction, RowActions } from "../settings/row-actions";
import { toInputValue, toStoredValue } from "../structure/labels";
import { ErrorNotice, FormError } from "../structure/parts";
import { SeatResponsibilities } from "../structure/responsibilities";
import { localDay, useSeatPeople } from "../work/model";
import { NewTaskDialog } from "../work/work-dialogs";
import { boardsQuery, invalidateWork } from "../work/work-queries";
import type { SeatDimensions } from "./chart-dimensions";
import { LENS_HINTS, LENS_LABELS } from "./chart-lenses";
import { chartKeys } from "./chart-queries";
import { inRange, Step, StepPair, useSaveLensSettings } from "./chart-settings";
import { COMPLETENESS_LABELS, TRACK_BAND, TRACK_LABELS, TRACKS, type Track } from "./keystones";
import { PriorityDetailModal, PriorityDialog } from "./priority-dialog";
import { PageArrow, SectionModal } from "./section-modal";

/** The panel's configurable sections (SEAT-DRAWER-UX: the setup list is the ring's popover). */
export type PanelSection =
  | "overall"
  | "performance"
  | "execution"
  | "responsibilities"
  | "tasks"
  | "finance"
  | "keystones";

type SeatRef = { id: string; title: string; status: string };

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** Every page of a cursor-paged list; past 50 pages it fails rather than show a partial list. */
async function everyPage<T>(
  read: (cursor?: string) => Promise<{ data: T[]; next_cursor?: string | null }>,
): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < 50; i++) {
    const p = await read(cursor);
    out.push(...p.data);
    if (!p.next_cursor) return out;
    cursor = p.next_cursor;
  }
  throw new Error("Too many rows to show here: open the full list instead.");
}

// Seat setup ----------------------------------------------------------------------------------

/** Items read from a gated dimension: unknown (not "in place") when the viewer may not see it. */
const GATED_ITEM: Partial<Record<CompletenessItem, (d: SeatDimensions) => boolean>> = {
  measurable: (d) => d.kpi !== undefined,
  finance: (d) => d.cost !== undefined,
  keystone: (d) => d.keystones !== undefined,
};

/**
 * SEAT-DRAWER-UX §3: the seat's setup checklist, in the completeness popover behind the header's
 * ring (it replaced the Seat setup modal): each required item in place, missing (with its add)
 * or hidden.
 */
export function SetupChecklist({
  dims,
  required,
  actionFor,
}: {
  dims: SeatDimensions | undefined;
  /** The company's completeness rules (which items count); absent: the missing items only. */
  required: Partial<Record<CompletenessItem, boolean>> | undefined;
  actionFor: (item: CompletenessItem) => ReactNode;
}) {
  const c = dims?.completeness;
  const missing = new Set(c?.missing ?? []);
  const items: CompletenessItem[] = required
    ? (Object.keys(COMPLETENESS_LABELS) as CompletenessItem[]).filter((i) => required[i])
    : [...missing];
  return (
    <>
      <ul className="flex flex-col gap-2" data-slot="setup-checklist">
        {items.map((item) => {
          const gate = GATED_ITEM[item];
          const state = missing.has(item)
            ? "missing"
            : dims && gate && !gate(dims)
              ? "hidden"
              : "done";
          return (
            <li
              key={item}
              data-item={item}
              data-state={state}
              className="flex min-w-0 items-center gap-2 text-sm leading-6"
            >
              {state === "done" ? (
                <CheckIcon aria-label="In place" className="size-4 shrink-0 text-success-text" />
              ) : state === "hidden" ? (
                <LockIcon aria-label="Shown to others" className="size-4 shrink-0 text-faint" />
              ) : (
                <LensBandDot band="red" aria-label="Missing" />
              )}
              <span
                className={cn(
                  "min-w-0 flex-1 truncate",
                  state === "done" ? "text-muted-foreground" : "text-secondary",
                )}
              >
                {COMPLETENESS_LABELS[item]}
              </span>
              {state === "missing" ? actionFor(item) : null}
            </li>
          );
        })}
      </ul>
      {c && c.hidden > 0 ? (
        <p className="flex items-center gap-1.5 text-xs text-faint">
          <LockIcon aria-hidden="true" className="size-3" />
          {plural(c.hidden, "missing item")} in a dimension shown to others.
        </p>
      ) : null}
    </>
  );
}

// Performance ---------------------------------------------------------------------------------

export function PerformanceModal({
  api,
  company,
  seat,
  holders,
  week,
  manager,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  seat: SeatRef;
  holders: readonly SeatHolder[];
  week: string;
  /** A manager edits the seat's KPIs and types any week's value; the seat's holder otherwise
   *  reports its own numbers, as the server's self-report scope lists them (`me/numbers`). */
  manager: boolean;
  onClose: () => void;
}) {
  return (
    <SectionModal
      title="Performance"
      info="The seat's KPIs and this week's values. KPIs belong to the seat: a new holder carries them."
      description={seat.title}
      onClose={onClose}
      slot="performance-modal"
      size={manager ? "xl" : "lg"}
    >
      {manager ? (
        <>
          <SeatKpisPanel
            api={api}
            company={company}
            seatId={seat.id}
            seatTitle={seat.title}
            holders={holders}
            week={week}
            readOnly={seat.status === "closed"}
          />
          <WeekEntry api={api} company={company} seatId={seat.id} week={week} manager />
        </>
      ) : (
        <HolderNumbers api={api} company={company} seatId={seat.id} />
      )}
      <ScorecardLink company={company} seatId={seat.id} />
    </SectionModal>
  );
}

/** The holder's own numbers for this seat (My numbers' rows: the period, the value, why). */
function HolderNumbers({
  api,
  company,
  seatId,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
}) {
  const numbers = useQuery(myNumbersQuery(api, company.id));
  const mine = numbers.data?.seats.find((s) => s.seat_id === seatId);
  if (numbers.isPending) return <Loading label="Loading your numbers" rows={2} />;
  if (numbers.isError) return <ErrorNotice what="Your numbers" error={numbers.error} />;
  if (!mine || !numbers.data)
    return <p className="text-xs text-faint">No number for you to report on this seat.</p>;
  return (
    <SeatNumbers
      api={api}
      company={company}
      seat={mine}
      currency={numbers.data.currency}
      today={localDay(new Date())}
      notes
    />
  );
}

/** The scorecard on this seat (with `kpi`: that measurable's cell focused): a page, marked ↗. */
export function ScorecardLink({
  company,
  seatId,
  kpi,
  className,
}: {
  company: CompanyAccess;
  seatId: string;
  kpi?: string;
  className?: string;
}) {
  return (
    <Link
      to="/c/$slug/kpis/scorecard"
      params={{ slug: company.slug }}
      search={(prev: Record<string, unknown>) => ({
        ...keepFocus(prev),
        s: `seat:${seatId}`,
        ...(kpi ? { kpi } : {}),
      })}
      className={cn("self-start text-xs text-primary-text hover:underline", className)}
      data-slot="open-scorecard"
      data-page-link=""
    >
      Open the scorecard
      <PageArrow />
    </Link>
  );
}

/** This week's value of each weekly, hand-entered measurable, typed in place (as the scorecard). */
export function WeekEntry({
  api,
  company,
  seatId,
  week,
  manager,
  only,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  week: string;
  manager: boolean;
  /** SEAT-DRAWER-CLICKS: this measurable alone (a drawer row's modal). */
  only?: string;
}) {
  const card = useQuery(seatScorecardQuery(api, company.id, seatId, week));
  const metrics = useAllMetrics(api, company.id);
  const detail = useQuery(companyQuery(api, company.id));
  const currency = detail.data?.reporting_currency ?? "USD";
  const today = currentWeek();
  const defs = new Map(metrics.all.map((m) => [m.id, m]));
  const rows = (card.data?.metrics ?? []).filter(
    (m) =>
      (only === undefined || m.metric_def_id === only) &&
      editableMetric(defs.get(m.metric_def_id), week, today),
  );
  return (
    <section className="flex flex-col gap-2" data-slot="week-entry" aria-labelledby="week-entry-h">
      <h3 id="week-entry-h" className="text-sm font-semibold">
        This week · {weekRangeLabel(week)}
      </h3>
      {card.isError ? (
        <ErrorNotice what="This week's scorecard" error={card.error} />
      ) : metrics.isError ? (
        <ErrorNotice what="The metrics" error={metrics.error} />
      ) : card.isPending || !metrics.complete ? (
        <Loading label="Loading this week's values" rows={2} />
      ) : rows.length === 0 ? (
        <p className="text-xs text-faint">No weekly number to enter by hand.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {rows.map((m) => (
            <EntryRow
              key={m.metric_def_id}
              api={api}
              company={company}
              seatId={seatId}
              week={week}
              today={today}
              manager={manager}
              currency={currency}
              m={m}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function EntryRow({
  api,
  company,
  seatId,
  week,
  today,
  manager,
  currency,
  m,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  week: string;
  today: string;
  manager: boolean;
  currency: string;
  m: ScorecardMetric;
}) {
  const qc = useQueryClient();
  const id = useId();
  const [draft, setDraft] = useState(
    m.actual === null ? "" : toInputValue(m.actual, m.unit, currency),
  );
  const [reason, setReason] = useState("");
  const stored = toStoredValue(draft, m.unit, currency);
  const unchanged = stored !== null && m.actual !== null && Number(m.actual) === stored;
  const needsReason =
    correctionNeedsReason({ hadValue: m.actual !== null, column: week, current: today, manager }) &&
    !unchanged;
  const save = useMutation({
    mutationFn: (value: number) =>
      api.client.observations.create({
        params: { co: company.id },
        body: {
          metric_def_id: m.metric_def_id,
          subject_kind: "seat",
          subject_id: seatId,
          period_type: "week",
          period_key: week,
          value: String(value),
          note: reason.trim() || null,
        },
      }),
    onSuccess: async () => {
      setReason("");
      toast.add({ title: `${m.name} saved`, type: "success" });
      const co = company.id;
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["company", co, "seat", seatId] }),
        qc.invalidateQueries({ queryKey: chartKeys.orgChart(co) }),
        qc.invalidateQueries({ queryKey: ["company", co, "scorecard"] }),
        qc.invalidateQueries({ queryKey: keys.myNumbers(co) }),
        // The KPI modal's history (SEAT-DRAWER-UX §2.3).
        qc.invalidateQueries({ queryKey: ["company", co, "observations"] }),
      ]);
    },
  });
  const can = stored !== null && !unchanged && (!needsReason || reason.trim() !== "");
  return (
    <li className="flex flex-col gap-1" data-entry={m.metric_def_id}>
      <form
        className="flex min-w-0 flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (can && stored !== null && !save.isPending) save.mutate(stored);
        }}
      >
        <label htmlFor={`${id}-v`} className="min-w-0 flex-1 truncate text-sm text-secondary">
          {m.name}
          {m.target !== null ? (
            <span className="text-xs text-faint"> · goal {formatCardMetric(m.target, m.unit)}</span>
          ) : null}
        </label>
        <Input
          id={`${id}-v`}
          inputMode="decimal"
          className="h-8 w-28 tabular-nums"
          value={draft}
          placeholder="—"
          aria-invalid={draft.trim() !== "" && stored === null ? true : undefined}
          onChange={(e) => setDraft(e.target.value)}
        />
        {/* A note goes with every value: a correction's reason (required), or the evidence a
            reporting duty asks for. */}
        <Input
          aria-label={
            needsReason
              ? `Why ${m.name} changes from ${formatMetricValue(m.actual, m.unit, currency)}`
              : `Note for ${m.name}`
          }
          placeholder={needsReason ? "Why it changes" : "Note (optional)"}
          className="h-8 w-56"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <Button type="submit" size="sm" disabled={!can || save.isPending}>
          {save.isPending ? "Saving…" : m.actual === null ? "Save" : "Correct"}
        </Button>
      </form>
      <FormError what={`Saving ${m.name}`} error={save.error} />
    </li>
  );
}

// Execution -----------------------------------------------------------------------------------

export function ExecutionModal({
  api,
  company,
  seat,
  holders,
  week,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  seat: SeatRef;
  holders: readonly SeatHolder[];
  week: string;
  onClose: () => void;
}) {
  const co = company.id;
  const id = useId();
  const detail = useQuery(companyQuery(api, co));
  const card = useQuery(seatScorecardQuery(api, co, seat.id, week));
  const set = useQuery(seatSetQuery(api, co, seat.id));
  const targets = useQuery(seatTargetsQuery(api, co, seat.id));
  const [editing, setEditing] = useState<{ row: SeatMetric; target: TargetRule | null } | null>(
    null,
  );
  const stored = lensThresholdsOf(detail.data?.settings.chart?.lenses?.thresholds).execution;
  // Only the steps typed here are saved (merged over the stored pair at save time).
  const [bands, setBands] = useState<Partial<{ green: number; amber: number }> | null>(null);
  const shown = { ...stored, ...bands };
  const valid =
    inRange(shown.green, 300) && inRange(shown.amber, 300) && shown.green >= shown.amber;
  const save = useSaveLensSettings(api, co);
  const submit = () => {
    if (!valid || !bands || !detail.data) return;
    save.mutate(
      { thresholds: { execution: bands } },
      {
        onSuccess: () => {
          toast.add({ title: "Execution bands saved", type: "success" });
          onClose();
        },
      },
    );
  };
  const ruleOf = (metricId: string) =>
    (targets.data?.data ?? [])
      .filter((t) => t.metric_def_id === metricId && t.effective_to === null)
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] ?? null;
  const rows = card.data?.metrics ?? [];
  return (
    <SectionModal
      title="Execution"
      info={LENS_HINTS.execution}
      description={`${seat.title} · ${weekRangeLabel(week)}`}
      onClose={onClose}
      slot="execution-modal"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!detail.data || !bands || !valid || save.isPending}
            onClick={submit}
          >
            {save.isPending ? "Saving…" : "Save bands"}
          </Button>
        </>
      }
    >
      <section className="flex flex-col gap-2" aria-labelledby={`${id}-targets`}>
        <h3 id={`${id}-targets`} className="text-sm font-semibold">
          Targets
        </h3>
        {card.isError || set.isError || targets.isError ? (
          <ErrorNotice what="The targets" error={card.error ?? set.error ?? targets.error} />
        ) : card.isPending || set.isPending || targets.isPending ? (
          <Loading label="Loading the targets" rows={2} />
        ) : rows.length === 0 ? (
          <p className="text-xs text-faint">No measurables on this seat.</p>
        ) : (
          <ul className="flex flex-col gap-1" data-slot="execution-targets">
            {rows.map((m) => {
              const row = set.data?.data.find((r) => r.metric_def_id === m.metric_def_id);
              return (
                <li
                  key={m.metric_def_id}
                  data-target={m.metric_def_id}
                  className="group/row flex min-w-0 items-center gap-2 text-sm"
                >
                  <span className="min-w-0 flex-1 truncate text-secondary">{m.name}</span>
                  <span className="shrink-0 text-xs text-faint tabular-nums">
                    {formatCardMetric(m.actual, m.unit)}
                    {m.target !== null
                      ? ` / ${formatCardMetric(m.target, m.unit)}`
                      : " / no target"}
                    {" · "}
                    {m.attainment === null ? "—" : `${Math.round(m.attainment * 100)}%`}
                  </span>
                  {row && seat.status !== "closed" ? (
                    <RowActions>
                      <RowAction
                        icon={PencilIcon}
                        label={`Edit the target of ${m.name}`}
                        tip="Edit target"
                        onClick={() => setEditing({ row, target: ruleOf(m.metric_def_id) })}
                      />
                    </RowActions>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>
      <section className="flex flex-col gap-2" aria-labelledby={`${id}-bands`}>
        <h3 id={`${id}-bands`} className="text-sm font-semibold">
          Bands
        </h3>
        {detail.isError ? (
          <ErrorNotice what="The bands" error={detail.error} />
        ) : detail.isPending ? (
          <Loading label="Loading the bands" rows={1} />
        ) : (
          <StepPair>
            <LensBandDot band="green" /> ≥
            <Step
              id={`${id}-green`}
              label="Execution: green at or above"
              value={shown.green}
              onChange={(n) => setBands((b) => ({ ...b, green: n }))}
            />
            <LensBandDot band="amber" /> ≥
            <Step
              id={`${id}-amber`}
              label="Execution: amber at or above"
              value={shown.amber}
              onChange={(n) => setBands((b) => ({ ...b, amber: n }))}
            />
            <LensBandDot band="red" /> below
          </StepPair>
        )}
        <p className="text-xs text-faint">Company-wide: every seat's Execution reads these.</p>
        {bands && !valid ? (
          <p role="alert" className="text-xs text-danger-text">
            0–300, green at or above amber.
          </p>
        ) : null}
        <FormError what="Saving the bands" error={save.error} />
      </section>
      {editing && set.data ? (
        <KpiAssignDialog
          api={api}
          company={company}
          seat={{
            seatId: seat.id,
            seatTitle: seat.title,
            holderText: holderText(holders),
            agent: holders.some((h) => h.kind === "digital_employee"),
          }}
          onSeat={set.data.data}
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => toast.add({ title: "Target saved", type: "success" })}
        />
      ) : null}
    </SectionModal>
  );
}

// Responsibilities ----------------------------------------------------------------------------

/** The seat's measurables a responsibility may link (undefined while off or loading). */
export function useSeatMeasurables(api: Api, co: string, seatId: string, on: boolean) {
  const set = useQuery({ ...seatSetQuery(api, co, seatId), enabled: on });
  const metrics = useAllMetrics(api, co, on);
  const names = new Map(metrics.all.map((m) => [m.id, m.name]));
  return on && set.data
    ? set.data.data.map((r) => ({
        id: r.metric_def_id,
        name: names.get(r.metric_def_id) ?? "Measurable",
      }))
    : undefined;
}

export function ResponsibilitiesModal({
  api,
  company,
  seat,
  kpiOn,
  readOnly = false,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  seat: SeatRef;
  /** The KPI add-on is on: the seat's measurables are offered to link. */
  kpiOn: boolean;
  /** SEAT-DRAWER-CLICKS: a viewer who may not change them ("+N more" opens the full list). */
  readOnly?: boolean;
  onClose: () => void;
}) {
  const measurables = useSeatMeasurables(api, company.id, seat.id, kpiOn && !readOnly);
  return (
    <SectionModal
      title="Responsibilities"
      info="What the seat answers for. A responsibility with a linked measurable counts as covered."
      description={seat.title}
      onClose={onClose}
      slot="responsibilities-modal"
    >
      <SeatResponsibilities
        api={api}
        company={company}
        seatId={seat.id}
        readOnly={readOnly}
        measurables={measurables}
      />
      <SeatPageLink company={company} seatId={seat.id} />
    </SectionModal>
  );
}

function SeatPageLink({ company, seatId }: { company: CompanyAccess; seatId: string }) {
  return (
    <Link
      to="/c/$slug/kpis/chart/seat/$id"
      params={{ slug: company.slug, id: seatId }}
      search={keepFocus}
      className="self-start text-xs text-primary-text hover:underline"
      data-slot="open-seat-page"
      data-page-link=""
    >
      Open the seat page
      <PageArrow />
    </Link>
  );
}

// Tasks ---------------------------------------------------------------------------------------

type TaskRow = Awaited<ReturnType<Api["client"]["tasks"]["list"]>>["data"][number];

export const seatTasksKey = (co: string, seat: string) =>
  ["company", co, "work", "seat-tasks", seat] as const;

/** Every task assigned to the seat, open and done (a manager's read, or the holder's own). */
export const seatTasksQuery = (api: Api, co: string, seat: string) => ({
  queryKey: seatTasksKey(co, seat),
  queryFn: () =>
    everyPage((cursor) =>
      api.client.tasks.list({
        params: { co },
        query: { assignee_seat_id: seat, limit: 200, ...(cursor ? { cursor } : {}) },
      }),
    ),
});

export function TasksModal({
  api,
  company,
  seat,
  onOpenTask,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  seat: SeatRef;
  /** SEAT-DRAWER-CLICKS: a task opens in Work's task drawer over the chart (this modal closes). */
  onOpenTask: (id: string) => void;
  onClose: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const people = useSeatPeople(api, co);
  const boards = useQuery(boardsQuery(api, co));
  const openBoards = (boards.data?.data ?? []).filter((b) => !b.archived_at);
  const tasks = useQuery(seatTasksQuery(api, co, seat.id));
  const open = (tasks.data ?? []).filter((t) => t.completed_at === null);
  const [adding, setAdding] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: seatTasksKey(co, seat.id) });
    void qc.invalidateQueries({ queryKey: chartKeys.orgChart(co) });
    invalidateWork(qc, co);
  };
  const reassign = useMutation({
    // One handoff (HANDOFF-LOG): the new seat assigned and this one removed together, logged.
    mutationFn: (v: { task: TaskRow; to: string }) =>
      api.client.tasks.assign({
        params: { co, id: v.task.id },
        body: { seat_ids: [v.to], handoff: { from_seat_id: seat.id } },
      }),
    onSuccess: (_d, v) => {
      toast.add({ title: `Reassigned to ${people.of(v.to).name}`, type: "success" });
    },
    onSettled: refresh,
  });
  const today = localDay(new Date());
  const others = people.all.filter((p) => !p.closed && p.seatId !== seat.id);
  return (
    <SectionModal
      title="Tasks"
      info="The open tasks assigned to this seat. Reassigning hands the task to another seat."
      description={seat.title}
      onClose={onClose}
      slot="tasks-modal"
    >
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 text-xs text-muted-foreground">
          {tasks.data ? plural(open.length, "open task") : ""}
        </p>
        <Button
          size="sm"
          onClick={() => setAdding(true)}
          disabled={!people.ready || !boards.data || openBoards.length === 0}
          data-slot="new-seat-task"
        >
          <PlusIcon data-icon="inline-start" aria-hidden="true" />
          New task
        </Button>
      </div>
      {tasks.isPending ? (
        <Loading label="Loading the seat's tasks" rows={3} />
      ) : tasks.isError ? (
        <ErrorNotice what="The seat's tasks" error={tasks.error} />
      ) : open.length === 0 ? (
        <EmptyState variant="card" title="No open tasks" />
      ) : (
        <ul className="flex flex-col divide-y divide-subtle" data-slot="seat-tasks">
          {open.map((t) => {
            const due = t.due_at ? localDay(t.due_at) : null;
            return (
              <li key={t.id} data-task={t.id} className="flex min-w-0 items-center gap-2 py-1.5">
                {/* SEAT-DRAWER-CLICKS: the task opens in Work's task drawer, over the chart. */}
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate rounded-sm text-left text-sm text-secondary hover:text-foreground hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                  title={t.title}
                  data-slot="task-open"
                  onClick={() => onOpenTask(t.id)}
                >
                  {t.title}
                </button>
                {due ? (
                  <Badge variant={due < today ? "danger" : "neutral"} className="shrink-0">
                    {due < today ? `Overdue · ${due}` : due}
                  </Badge>
                ) : null}
                <NativeSelect
                  size="sm"
                  aria-label={`Reassign ${t.title}`}
                  value=""
                  disabled={reassign.isPending || !people.ready}
                  onChange={(e) => {
                    if (e.target.value) reassign.mutate({ task: t, to: e.target.value });
                  }}
                  className="w-40 shrink-0"
                >
                  <NativeSelectOption value="">Reassign…</NativeSelectOption>
                  {others.map((p) => (
                    <NativeSelectOption key={p.seatId} value={p.seatId}>
                      {p.full}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </li>
            );
          })}
        </ul>
      )}
      <FormError what="Reassigning the task" error={reassign.error} />
      <Link
        to="/c/$slug/work"
        params={{ slug: company.slug }}
        className="self-start text-xs text-primary-text hover:underline"
        data-page-link=""
      >
        Open Work
        <PageArrow />
      </Link>
      {/* Mounted while the seat names are known (it presets the seat as assignee or delegate)
          and kept mounted when closed, so the create's answer still refreshes the list. */}
      {people.ready && openBoards.length > 0 ? (
        <NewTaskDialog
          api={api}
          company={company}
          boards={openBoards}
          people={people}
          open={adding}
          onOpenChange={setAdding}
          defaults={{ assignee: seat.id }}
          onCreated={refresh}
        />
      ) : null}
    </SectionModal>
  );
}

// Finance: SEAT-DRAWER-UX §4 moved the seat's budgets into the drawer's rows (seat-finance.tsx).

// Priorities ----------------------------------------------------------------------------------

export function PrioritiesModal({
  api,
  company,
  seat,
  quarter,
  goals,
  pending,
  error,
  readOnly = false,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  seat: SeatRef;
  quarter: string | null;
  /** The quarter's priorities (the panel's read). */
  goals: readonly Goal[] | undefined;
  pending: boolean;
  /** The panel's read failed. */
  error: unknown;
  /** SEAT-DRAWER-CLICKS: a viewer who may not change them ("View all" opens the full list). */
  readOnly?: boolean;
  onClose: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<"add" | Goal | null>(null);
  const [detail, setDetail] = useState<Goal | null>(null);
  // The chart's cards and panel, and KPIs › Priorities.
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: chartKeys.orgChart(co) });
    void qc.invalidateQueries({ queryKey: ["company", co, "priorities"] });
  };
  const track = useMutation({
    mutationFn: (v: { id: string; track: Track }) =>
      api.client.goals.update({ params: { co, id: v.id }, body: { track: v.track } }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.client.goals.delete({ params: { co, id } }),
    onSuccess: () => {
      toast.add({ title: "Priority removed", type: "success" });
      refresh();
    },
  });
  return (
    <SectionModal
      title="Priorities"
      info="What the seat delivers this quarter; its track feeds the Priorities lens."
      description={quarter ? `${seat.title} · ${quarter}` : seat.title}
      onClose={onClose}
      slot="priorities-modal"
    >
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 text-xs text-muted-foreground">
          {goals ? `${goals.length} ${goals.length === 1 ? "priority" : "priorities"}` : ""}
        </p>
        {readOnly ? null : (
          <Button size="sm" onClick={() => setDialog("add")} data-slot="add-priority">
            <PlusIcon data-icon="inline-start" aria-hidden="true" />
            Add a priority
          </Button>
        )}
      </div>
      {error ? (
        <ErrorNotice what="The priorities" error={error} />
      ) : pending ? (
        <Loading label="Loading the priorities" rows={2} />
      ) : !goals?.length ? (
        <p className="text-xs text-faint">No priorities this quarter.</p>
      ) : (
        <ul className="flex flex-col gap-1.5" data-slot="priorities-list">
          {goals.map((g) => (
            <li
              key={g.id}
              data-priority={g.id}
              className="group/row flex min-w-0 items-center gap-2 text-sm"
            >
              <LensBandDot band={TRACK_BAND[g.track]} aria-label={TRACK_LABELS[g.track]} />
              {/* SEAT-DRAWER-CLICKS: the title opens the priority: its editor, or (read only) its
                  milestones. */}
              <button
                type="button"
                className="min-w-0 flex-1 truncate rounded-sm text-left text-secondary hover:text-foreground hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                title={g.title}
                data-slot="priority-open"
                onClick={() => (readOnly ? setDetail(g) : setDialog(g))}
              >
                {g.title}
                {g.milestones.length ? (
                  <span className="text-xs text-faint">
                    {" "}
                    · {g.milestones.filter((m) => m.done_at !== null).length}/{g.milestones.length}
                  </span>
                ) : null}
              </button>
              {readOnly ? (
                <span className="shrink-0 text-xs text-faint">{TRACK_LABELS[g.track]}</span>
              ) : (
                <NativeSelect
                  size="sm"
                  aria-label={`Status of ${g.title}`}
                  value={g.track}
                  onChange={(e) => track.mutate({ id: g.id, track: e.target.value as Track })}
                  className="w-28 shrink-0"
                >
                  {TRACKS.map((t) => (
                    <NativeSelectOption key={t} value={t}>
                      {TRACK_LABELS[t]}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              )}
              {readOnly ? null : (
                <RowActions>
                  <RowAction
                    icon={PencilIcon}
                    label={`Edit ${g.title}`}
                    tip="Edit"
                    onClick={() => setDialog(g)}
                  />
                  <RowAction
                    icon={Trash2Icon}
                    label={`Remove ${g.title}`}
                    tip="Remove"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(g.id)}
                  />
                </RowActions>
              )}
            </li>
          ))}
        </ul>
      )}
      <FormError what="Changing the priority" error={track.error ?? remove.error} />
      <Link
        to="/c/$slug/kpis/priorities"
        params={{ slug: company.slug }}
        search={(prev: Record<string, unknown>) => ({
          ...keepFocus(prev),
          seat: seat.id,
          ...(quarter ? { quarter } : {}),
        })}
        className="self-start text-xs text-primary-text hover:underline"
        data-page-link=""
      >
        Open KPIs › Priorities
        <PageArrow />
      </Link>
      {detail ? (
        <PriorityDetailModal goal={detail} quarter={quarter} onClose={() => setDetail(null)} />
      ) : null}
      {dialog ? (
        <PriorityDialog
          api={api}
          co={co}
          seatId={seat.id}
          seatTitle={seat.title}
          quarter={quarter}
          {...(dialog === "add" ? {} : { current: dialog })}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </SectionModal>
  );
}

// Overall -------------------------------------------------------------------------------------

export function OverallModal({
  api,
  company,
  lenses,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  /** The lenses the company serves (a weight for each scored one). */
  lenses: readonly string[];
  onClose: () => void;
}) {
  const id = useId();
  const detail = useQuery(companyQuery(api, company.id));
  const save = useSaveLensSettings(api, company.id);
  // Only the weights typed here: saved over the stored ones, so another admin's change to the
  // others (or a refresh while this is open) is kept.
  const [draft, setDraft] = useState<Partial<Record<ScoredLens, number>> | null>(null);
  const stored = lensWeightsOf(detail.data?.settings.chart?.lenses?.weights);
  const weights = { ...stored, ...draft };
  const valid = SCORED_LENSES.every((l) => inRange(weights[l], 10));
  const submit = () => {
    if (!draft || !valid) return;
    save.mutate(
      { weights: draft },
      {
        onSuccess: () => {
          toast.add({ title: "Weights saved", type: "success" });
          onClose();
        },
      },
    );
  };
  return (
    <SectionModal
      title="Overall"
      info="The Overall score is the weighted mean of the scored lenses. 0 leaves a lens out. The weights are company-wide."
      description="Lens weights"
      onClose={onClose}
      slot="overall-modal"
      size="default"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={!draft || !valid || save.isPending} onClick={submit}>
            {save.isPending ? "Saving…" : "Save weights"}
          </Button>
        </>
      }
    >
      {detail.isPending ? (
        <Loading label="Loading the weights" rows={3} />
      ) : detail.isError ? (
        <ErrorNotice what="The weights" error={detail.error} />
      ) : (
        <ul className="flex flex-col gap-2" data-slot="overall-weights">
          {SCORED_LENSES.filter((l) => lenses.includes(l)).map((l) => (
            <li key={l} data-lens={l} className="flex items-center gap-3 text-sm">
              <span aria-hidden="true" className="size-2.5 rounded-[2px] bg-(--lens)" />
              <label htmlFor={`${id}-${l}`} className="min-w-0 flex-1 text-secondary">
                {LENS_LABELS[l]}
              </label>
              <StepPair>
                ×
                <Step
                  id={`${id}-${l}`}
                  label={`${LENS_LABELS[l]} weight`}
                  value={weights[l]}
                  max={10}
                  onChange={(n) => setDraft((d) => ({ ...d, [l]: n }))}
                />
                <span className="w-16 text-faint">{weights[l] === 0 ? "left out" : "0–10"}</span>
              </StepPair>
            </li>
          ))}
        </ul>
      )}
      {draft && !valid ? (
        <p role="alert" className="text-xs text-danger-text">
          Weights are 0–10.
        </p>
      ) : null}
      <FormError what="Saving the weights" error={save.error} />
    </SectionModal>
  );
}
